"use client";

import { Drawer, DrawerContent } from "../../shadcn/ui/drawer";

import { ArrowUpRight } from "lucide-react";
import { CollapsibleNavGroup } from "./CollapsibleNavGroup";
import { findActiveNav } from "./findActiveNav";
import { matchesPath } from "./matchesPath";
import Link from "next/link";
import { cn } from "@punchup/utils/cn";

interface PageNavDrawerLink {
  label: string;
  href: string;
  type?: "internal" | "external";
  // Unique identity used when href isn't a stable key (e.g. a group whose
  // href is empty). Falls back to href when undefined.
  id?: string;
  // When present, this entry renders as a collapsible section whose body
  // lists the children. The trigger itself doesn't navigate.
  children?: PageNavDrawerLink[];
}

interface PageNavDrawerMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  links: PageNavDrawerLink[];
  pathname: string;
  onLinkClick?: (link: PageNavDrawerLink, event?: React.MouseEvent) => void;
  footerLink?: {
    label: string;
    href: string;
  };
  matchSubPaths?: boolean;
  /**
   * Optional override for the active link. When provided, pathname-based
   * detection is bypassed. Mirrors the same prop on PageNavMenu.
   */
  activeHrefOverride?: string | null;
}

const drawerLinkKey = (link: PageNavDrawerLink): string => link.id ?? link.href;

export function PageNavDrawerMenu({
  isOpen,
  onOpenChange,
  links,
  pathname,
  onLinkClick,
  footerLink,
  matchSubPaths = false,
  activeHrefOverride,
}: PageNavDrawerMenuProps) {
  const commonLinkClasses = cn(
    "flex flex-col justify-center items-start border-b border-[#C7C8C9] -mb-px px-5 bg-transparent transition-colors duration-200 ease-in-out leading-tight",
    "border-none"
  );

  const activeLinkClasses =
    "border-b-2 border-primary-blue text-primary-blue font-semibold";

  // Longest matching href across links and their children. Shared with
  // PageNavMenu via findActiveNav so desktop and drawer highlighting agree.
  // Groups have no href of their own and never qualify; their children do.
  const activeHref =
    findActiveNav(links, pathname, { matchSubPaths, activeHrefOverride })
      ?.href ?? null;

  const handleLinkClick = (
    link: PageNavDrawerLink,
    event?: React.MouseEvent
  ) => {
    onLinkClick?.(link, event);
    if (link.type !== "external") {
      onOpenChange(false);
    }
  };

  const renderLink = (link: PageNavDrawerLink, isChild = false) => {
    const isActive = activeHref === link.href && link.href !== "";
    const isExternal = link.type === "external";
    return (
      <Link
        key={drawerLinkKey(link)}
        className={cn(
          commonLinkClasses,
          isActive && activeLinkClasses,
          isChild && "pl-8"
        )}
        onClick={(e) => handleLinkClick(link, e)}
        href={link.href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        <span
          className={cn(
            "flex flex-row items-center pb-1.5 pt-3",
            isExternal ? "gap-x-1" : "gap-x-3"
          )}
        >
          {link.label}
          {isExternal && <ArrowUpRight className="w-5 h-5" />}
        </span>
      </Link>
    );
  };

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="z-[99] font-regular flex items-center gap-x-4">
        <div className="h-[75dvh] w-full pt-2 pb-8 overflow-y-scroll">
          <nav className="flex flex-col gap-1.5 w-full text-xl text-secondary-blue font-medium">
            {links.map((link) => {
              if (link.children && link.children.length > 0) {
                // Open the group by default when the current path matches one
                // of its children, so users land on an expanded section.
                const hasActiveChild = link.children.some((child) =>
                  matchesPath(child.href, pathname, matchSubPaths)
                );
                return (
                  <CollapsibleNavGroup
                    key={drawerLinkKey(link)}
                    label={link.label}
                    triggerClassName={commonLinkClasses}
                    defaultOpen={hasActiveChild}
                  >
                    {link.children.map((child) => renderLink(child, true))}
                  </CollapsibleNavGroup>
                );
              }
              return renderLink(link);
            })}

            {footerLink && (
              <>
                <div className="h-px my-1 bg-neutral-200" />
                <Link
                  className={cn(commonLinkClasses)}
                  href={footerLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onOpenChange(false)}
                >
                  <span className="flex flex-row items-center gap-x-1 pb-1.5 pt-3">
                    {footerLink.label}
                    <ArrowUpRight className="w-5 h-5" />
                  </span>
                </Link>
              </>
            )}
          </nav>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
